import * as React from 'react';
import {
  Ban,
  CheckCircle2,
  Clock,
  MinusCircle,
  Send,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STATUS_LABELS } from '@/lib/status-labels';

/**
 * Pill vs Tag — semantic contract (per DESIGN.md "Tags vs Pills"):
 *
 *   <Pill>  → STATE      e.g. Paid, Pending, Submitted, Rejected, Active, Linked.
 *   <Tag>   → METADATA   e.g. team codes, niche codes, category codes, channel codes.
 *
 * Pills carry an icon + sentence-case label by default; the icon comes from
 * the tone unless the caller passes their own. Status loses semantic weight
 * when status and metadata share the same primitive — see the original
 * critique snapshot for background.
 */
export type PillTone =
  | 'pending'
  | 'success'
  | 'paid'
  | 'verified'
  | 'info'
  | 'submitted'
  | 'destructive'
  | 'rejected'
  | 'failed'
  | 'muted'
  | 'notfound';

interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone: PillTone;
  /** Optional icon override. If omitted, the tone picks its default Lucide glyph. */
  icon?: LucideIcon | false;
}

const TONE_ICONS: Record<PillTone, LucideIcon> = {
  pending: Clock,
  success: CheckCircle2,
  paid: CheckCircle2,
  verified: ShieldCheck,
  info: Send,
  submitted: Send,
  destructive: XCircle,
  rejected: Ban,
  failed: XCircle,
  muted: MinusCircle,
  notfound: MinusCircle,
};

/**
 * Status pill — sentence-case label with a tone-matched icon. Pass any
 * status code (`PENDING`, `AWAITING_RATE`, `IN_PROGRESS`) and it title-cases
 * automatically. Pass `icon={false}` to suppress the leading glyph for
 * dense contexts where word-only is enough.
 */
export function Pill({ tone, icon, className, children, ...props }: PillProps): JSX.Element {
  const Icon = icon === false ? null : icon ?? TONE_ICONS[tone];
  const label = typeof children === 'string' ? formatStatusLabel(children) : children;
  return (
    <span className={cn('pill', `pill-${tone}`, className)} {...props}>
      {Icon && <Icon className="h-3 w-3 shrink-0" strokeWidth={2.25} />}
      {label}
    </span>
  );
}

/**
 * Convenience: pick a tone for the union of statuses the app uses across
 * invites, payouts, and team/category active flags. Anything unknown falls
 * back to a muted tone.
 */
export function toneFor(status: string): PillTone {
  switch (status) {
    case 'PAID':
    case 'CONVERTED':
    case 'ROSTERED':
    case 'COMPLETED':
    case 'Active':
      return 'paid';
    case 'VERIFIED':
      return 'verified';
    case 'PENDING':
    case 'REQUESTED':
      return 'pending';
    case 'SUBMITTED':
    case 'IN_PROGRESS':
    case 'AWAITING_RESPONSE':
    case 'AWAITING_RATE':
      return 'submitted';
    case 'REJECTED':
      return 'rejected';
    case 'FAILED':
    case 'CANCELLED':
      return 'failed';
    case 'Inactive':
      return 'muted';
    default:
      return 'muted';
  }
}

/**
 * Turn a backend status code into a human label. SCREAMING_SNAKE → Title Case;
 * already-cased strings ("Active") pass through.
 */
function formatStatusLabel(raw: string): string {
  if (!raw) return raw;
  // Explicit overrides in src/lib/status-labels.ts take precedence over the
  // generic title-case fallback. That's the single knob to rename a status
  // across every pill in the app (e.g. VERIFIED → Joined).
  const override = STATUS_LABELS[raw];
  if (override) return override;
  // Mixed-case strings (e.g. "Active") stay as-is.
  if (raw !== raw.toUpperCase()) return raw;
  return raw
    .toLowerCase()
    .split('_')
    .map((w) => (w.length === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}
